import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Bell, Star, Clock, Heart, Compass, ShoppingBag, User,
    MapPin, Loader2, Coffee, Pizza, Beef, Soup, Salad, IceCream2, Map as MapIcon,
    ChevronRight, Sparkles, Flame, Tag, Percent
} from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { AISearchBar } from "../components/AISearchBar";
import { CartBar } from "../components/CartBar";

const CATEGORIES = [
    { id: "all",     label: "Όλα",     icon: Sparkles, color: "from-amber-400 to-amber-600" },
    { id: "burger",  label: "Burger",  icon: Beef,     color: "from-rose-400 to-rose-600" },
    { id: "pizza",   label: "Pizza",   icon: Pizza,    color: "from-orange-400 to-red-500" },
    { id: "asian",   label: "Asian",   icon: Soup,     color: "from-emerald-400 to-emerald-600" },
    { id: "coffee",  label: "Coffee",  icon: Coffee,   color: "from-amber-700 to-yellow-900" },
    { id: "street",  label: "Street",  icon: Salad,    color: "from-lime-400 to-emerald-500" },
    { id: "dessert", label: "Γλυκά",   icon: IceCream2,color: "from-pink-400 to-fuchsia-500" },
];

const PROMOS = [
    { id: 1, title: "Δωρεάν Delivery", subtitle: "σε παραγγελίες άνω των €15", icon: Tag, gradient: "from-[#f4b339] via-[#e89c1f] to-[#c98e15]" },
    { id: 2, title: "−20% Welcome", subtitle: "Πρώτη παραγγελία · κωδικός WELCOME20", icon: Percent, gradient: "from-rose-500 via-orange-500 to-[#f4b339]" },
    { id: 3, title: "Express Σε 20'", subtitle: "Γρήγορη παράδοση από κορυφαία μαγαζιά", icon: Flame, gradient: "from-fuchsia-500 via-pink-500 to-rose-500" },
];

export const FeedHome = () => {
    const navigate = useNavigate();
    const { count } = useCart();
    const { user } = useAuth();
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState("all");
    const [favs, setFavs] = useState(() => JSON.parse(localStorage.getItem("favs") || "[]"));

    useEffect(() => {
        api.get("/stores").then(({ data }) => {
            setStores(data || []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const filteredStores = useMemo(() => {
        if (activeCategory === "all") return stores;
        return stores.filter((s) => (s.categories || []).includes(activeCategory) || s.cuisine === activeCategory);
    }, [stores, activeCategory]);

    const trendingStores = useMemo(() => {
        return [...filteredStores].filter((s) => s.is_hot || s.rating >= 4.7).slice(0, 8);
    }, [filteredStores]);

    const fastStores = useMemo(() => {
        return [...filteredStores].sort((a, b) => (a.delivery_minutes || 99) - (b.delivery_minutes || 99)).slice(0, 8);
    }, [filteredStores]);

    const newStores = useMemo(() => {
        return [...filteredStores].slice(-6).reverse();
    }, [filteredStores]);

    const toggleFav = (storeId, e) => {
        e?.stopPropagation();
        const next = favs.includes(storeId) ? favs.filter((x) => x !== storeId) : [...favs, storeId];
        setFavs(next);
        localStorage.setItem("favs", JSON.stringify(next));
    };

    return (
        <div data-testid="feed-home" className="min-h-screen cyber-bg text-white pb-32">
            {/* HEADER */}
            <header className="sticky top-0 z-[500] cyber-bg/95 backdrop-blur-xl border-b border-white/5 px-5 pt-4 pb-3">
                <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                        <p className="text-[9px] tracking-[0.32em] text-white/50 font-mono uppercase flex items-center gap-1">
                            <MapPin size={10} /> ΘΕΣΣΑΛΟΝΙΚΗ ▾
                        </p>
                        <h1 className="font-heading tracking-tight text-3xl mt-0.5" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                            <span className="text-white">Thess</span>
                            <span className="gold-text">·</span>
                            <span className="text-white">Eats</span>
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="hdr-map"
                            onClick={() => navigate("/map")}
                            className="h-10 w-10 grid place-items-center rounded-full glass-gold border border-[var(--gold)]/20"
                            aria-label="Map view"
                        >
                            <MapIcon size={16} className="text-[var(--gold-light)]" />
                        </button>
                        <button data-testid="hdr-bell" className="relative h-10 w-10 grid place-items-center rounded-full glass-gold border border-[var(--gold)]/20">
                            <Bell size={16} className="text-[var(--gold-light)]" />
                            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[var(--gold)] shadow-[0_0_8px_var(--gold)]" />
                        </button>
                    </div>
                </div>

                {/* AI Search */}
                <AISearchBar />
            </header>

            {/* PROMO CAROUSEL */}
            <section className="px-5 mt-5">
                <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-5 px-5 pb-2 snap-x">
                    {PROMOS.map((p) => {
                        const Icon = p.icon;
                        return (
                            <article
                                key={p.id}
                                data-testid={`promo-${p.id}`}
                                className={`snap-start shrink-0 w-[78%] sm:w-[55%] h-32 rounded-3xl p-5 bg-gradient-to-br ${p.gradient} relative overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.5)]`}
                            >
                                <div className="absolute -right-6 -bottom-6 opacity-20">
                                    <Icon size={130} strokeWidth={1.5} />
                                </div>
                                <div className="relative">
                                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-black/70">// promo</p>
                                    <h3 className="font-heading text-2xl text-black font-bold leading-tight mt-1" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                                        {p.title}
                                    </h3>
                                    <p className="text-xs text-black/80 mt-1">{p.subtitle}</p>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            {/* CATEGORY GRID */}
            <section className="px-5 mt-6">
                <h2 className="text-sm font-bold text-white/90 mb-3 flex items-center justify-between">
                    <span>Κατηγορίες</span>
                </h2>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-3">
                    {CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        const on = activeCategory === c.id;
                        return (
                            <button
                                key={c.id}
                                data-testid={`cat-${c.id}`}
                                onClick={() => setActiveCategory(c.id)}
                                className={`group flex flex-col items-center gap-1.5 active:scale-95 transition`}
                            >
                                <div className={`h-16 w-16 grid place-items-center rounded-2xl bg-gradient-to-br ${c.color} ${on ? "ring-2 ring-[var(--gold)] ring-offset-2 ring-offset-[#0B0B0F]" : "opacity-90 group-hover:opacity-100"} shadow-lg transition`}>
                                    <Icon size={24} strokeWidth={2} className="text-black" />
                                </div>
                                <span className={`text-[10px] font-mono uppercase tracking-wider ${on ? "text-[var(--gold-light)]" : "text-white/70"}`}>
                                    {c.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </section>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="animate-spin text-[var(--gold-light)]" size={32} />
                </div>
            ) : (
                <>
                    {/* TRENDING — horizontal carousel */}
                    {trendingStores.length > 0 && (
                        <HCarousel
                            title="Trending τώρα"
                            icon={Flame}
                            stores={trendingStores}
                            favs={favs}
                            onFav={toggleFav}
                            onClick={(slug) => navigate(`/store/${slug}`)}
                            testidPrefix="trending"
                        />
                    )}

                    {/* FAST — horizontal carousel */}
                    {fastStores.length > 0 && (
                        <HCarousel
                            title="Παράδοση σε λίγα λεπτά"
                            icon={Clock}
                            stores={fastStores}
                            favs={favs}
                            onFav={toggleFav}
                            onClick={(slug) => navigate(`/store/${slug}`)}
                            testidPrefix="fast"
                            badge={(s) => `${s.delivery_minutes}'`}
                        />
                    )}

                    {/* NEW STORES */}
                    {newStores.length > 0 && (
                        <HCarousel
                            title="Νέα στο Thess·Eats"
                            icon={Sparkles}
                            stores={newStores}
                            favs={favs}
                            onFav={toggleFav}
                            onClick={(slug) => navigate(`/store/${slug}`)}
                            testidPrefix="new"
                        />
                    )}

                    {/* ALL STORES — vertical list */}
                    <section className="px-5 mt-8">
                        <h2 className="text-sm font-bold text-white/90 mb-3 flex items-center justify-between">
                            <span>Όλα τα μαγαζιά · {filteredStores.length}</span>
                        </h2>
                        <div className="space-y-3">
                            {filteredStores.map((s) => (
                                <RestaurantCardWide
                                    key={s.id}
                                    store={s}
                                    fav={favs.includes(s.id)}
                                    onFav={(e) => toggleFav(s.id, e)}
                                    onClick={() => navigate(`/store/${s.slug}`)}
                                />
                            ))}
                        </div>
                    </section>
                </>
            )}

            {/* PERSISTENT CART BAR (above bottom nav) */}
            <CartBar bottomOffset={72} />

            {/* BOTTOM NAV */}
            <nav data-testid="bottom-nav" className="fixed bottom-0 left-0 right-0 z-[460] glass-gold border-t border-[var(--gold)]/20">
                <div className="flex justify-around items-center pb-[env(safe-area-inset-bottom)] py-3 px-2">
                    <NavTab id="explore" label="Αρχική" icon={Compass} active />
                    <NavTab id="map" label="Χάρτης" icon={MapIcon} onClick={() => navigate("/map")} />
                    <NavTab id="favorites" label="Αγαπημένα" icon={Heart} onClick={() => navigate("/offers")} />
                    <NavTab id="orders" label="Παραγγελίες" icon={ShoppingBag} count={count} onClick={() => navigate("/orders")} />
                    <NavTab id="profile" label="Προφίλ" icon={User} onClick={() => navigate(user ? "/orders" : "/login")} />
                </div>
            </nav>
        </div>
    );
};

// === Horizontal carousel of restaurant cards ===
const HCarousel = ({ title, icon: Icon, stores, favs, onFav, onClick, testidPrefix, badge }) => (
    <section className="mt-8" data-testid={`section-${testidPrefix}`}>
        <div className="flex items-center justify-between px-5 mb-3">
            <h2 className="text-sm font-bold text-white/90 flex items-center gap-2">
                <Icon size={14} className="text-[var(--gold-light)]" />
                {title}
            </h2>
            <button className="text-[10px] font-mono uppercase tracking-widest text-white/50 hover:text-[var(--gold-light)] flex items-center gap-1">
                Όλα <ChevronRight size={11} />
            </button>
        </div>
        <div className="flex gap-3 overflow-x-auto scrollbar-hide px-5 pb-2 snap-x">
            {stores.map((s) => (
                <RestaurantCardCompact
                    key={s.id}
                    store={s}
                    fav={favs.includes(s.id)}
                    onFav={(e) => onFav(s.id, e)}
                    onClick={() => onClick(s.slug)}
                    badgeText={badge ? badge(s) : null}
                    testid={`${testidPrefix}-${s.id}`}
                />
            ))}
        </div>
    </section>
);

// === Compact card for carousels ===
const RestaurantCardCompact = ({ store, fav, onFav, onClick, badgeText, testid }) => (
    <article
        data-testid={testid}
        onClick={onClick}
        className="snap-start shrink-0 w-64 cursor-pointer group active:scale-[0.98] transition"
    >
        <div className="relative h-40 rounded-3xl overflow-hidden">
            <img src={store.image_url || store.logo_url} alt={store.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            {store.is_hot && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-[var(--gold)] text-[#0B0B0F] px-2 py-1 rounded-full">
                    <Flame size={10} /> HOT
                </span>
            )}
            {badgeText && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold bg-black/70 backdrop-blur-md text-white px-2 py-1 rounded-full">
                    <Clock size={10} /> {badgeText}
                </span>
            )}
            <button
                onClick={onFav}
                className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/40 backdrop-blur-md hover:bg-black/60 transition"
            >
                <Heart size={14} fill={fav ? "var(--gold-light)" : "none"} className={fav ? "text-[var(--gold-light)]" : "text-white"} />
            </button>
            <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="font-heading text-lg text-white leading-tight truncate" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                    {store.name}
                </h3>
                <div className="flex items-center gap-2.5 text-[10px] text-white/85 mt-0.5">
                    <span className="flex items-center gap-1 text-[var(--gold-light)]">
                        <Star size={9} fill="currentColor" />
                        <span className="font-bold">{store.rating?.toFixed(1)}</span>
                    </span>
                    <span className="text-white/40">·</span>
                    <span className="capitalize truncate">{store.cuisine}</span>
                </div>
            </div>
        </div>
        <div className="flex items-center gap-2 mt-2 px-1 text-[10px] text-white/60">
            <span className="flex items-center gap-1">
                <Clock size={10} /> {store.delivery_minutes}'
            </span>
            <span className="text-white/30">·</span>
            <span>€{store.delivery_fee?.toFixed(2)}</span>
            {store.delivery_fee === 0 && (
                <span className="text-emerald-400 font-bold">Δωρεάν</span>
            )}
        </div>
    </article>
);

// === Wide horizontal card for the main list ===
const RestaurantCardWide = ({ store, fav, onFav, onClick }) => (
    <article
        data-testid={`store-card-${store.id}`}
        onClick={onClick}
        className="cursor-pointer group flex gap-3 glass-gold rounded-3xl overflow-hidden border border-white/5 hover:border-[var(--gold)]/40 active:scale-[0.99] transition"
    >
        <div className="relative w-32 h-32 shrink-0">
            <img src={store.image_url || store.logo_url} alt={store.name} className="w-full h-full object-cover" />
            {store.is_hot && (
                <span className="absolute top-2 left-2 text-[8px] font-bold uppercase tracking-widest bg-[var(--gold)] text-[#0B0B0F] px-1.5 py-0.5 rounded-full">
                    HOT
                </span>
            )}
        </div>
        <div className="flex-1 py-3 pr-3 min-w-0 relative">
            <button
                onClick={onFav}
                className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full hover:bg-white/10 transition"
            >
                <Heart size={13} fill={fav ? "var(--gold-light)" : "none"} className={fav ? "text-[var(--gold-light)]" : "text-white/50"} />
            </button>
            <h3 className="font-heading text-lg text-white leading-tight truncate pr-7" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                {store.name}
            </h3>
            <p className="text-[10px] text-white/50 capitalize truncate mt-0.5">
                {store.cuisine} · {store.tagline?.split('·')[0]}
            </p>
            <div className="flex items-center gap-3 mt-2 text-[11px]">
                <span className="flex items-center gap-1 text-[var(--gold-light)]">
                    <Star size={11} fill="currentColor" />
                    <span className="font-bold">{store.rating?.toFixed(1)}</span>
                    <span className="text-white/40">({store.rating_count})</span>
                </span>
                <span className="flex items-center gap-1 text-white/65">
                    <Clock size={11} />
                    {store.delivery_minutes}'
                </span>
            </div>
            <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-white/55">
                    Delivery <span className="text-white/80 font-bold">€{store.delivery_fee?.toFixed(2)}</span>
                </span>
                {store.open_now ? (
                    <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">Ανοιχτό</span>
                ) : (
                    <span className="text-[10px] text-white/40 uppercase tracking-widest">Κλειστό</span>
                )}
            </div>
        </div>
    </article>
);

const NavTab = ({ label, icon: Icon, active, onClick, count }) => (
    <button
        data-testid={`nav-${label}`}
        onClick={onClick}
        className={`flex-1 max-w-[80px] flex flex-col items-center justify-center gap-0.5 h-12 rounded-xl transition active:scale-95 ${active ? "text-[var(--gold-light)]" : "text-white/55 hover:text-white"}`}
    >
        <div className="relative">
            <Icon size={20} strokeWidth={active ? 2.5 : 2} />
            {count > 0 && (
                <span className="absolute -top-1.5 -right-2.5 min-w-[14px] h-3.5 px-1 grid place-items-center rounded-full bg-[var(--gold)] text-[#0B0B0F] text-[8px] font-mono font-bold">
                    {count}
                </span>
            )}
        </div>
        <span className="text-[8px] font-mono uppercase tracking-[0.15em] font-bold">{label}</span>
    </button>
);

export default FeedHome;
